import { useCallback, useEffect, useRef, useState } from "react";

// Per-(activityId, field) save-state machine for the mobile Actual cells.
// States: 'idle' | 'saving' | 'saved' | 'error'. 'saved' auto-reverts to
// 'idle' after AUTO_REVERT_MS so the confirmation check fades on its own.
//
// PURE STATE — no API calls. Phase 2 owns the actual save (saveActualDate);
// it calls markSaving()/markSaved()/markError() around that call. Keeping the
// machine here (not inside the cell) lets one parent track many cells and lets
// the save outcome outlive a cell unmount/remount during a list re-render.

const AUTO_REVERT_MS = 2000;

const keyOf = (activityId, field) => `${activityId}::${field}`;

export function useActualSaveState() {
  // Map<key, state>. Stored in a plain object in state so consumers re-render.
  const [states, setStates] = useState({});
  // Active auto-revert timers, keyed identically, so we can clear/replace them.
  const timersRef = useRef(new Map());

  const clearTimer = useCallback((key) => {
    const t = timersRef.current.get(key);
    if (t) {
      clearTimeout(t);
      timersRef.current.delete(key);
    }
  }, []);

  const setState = useCallback((activityId, field, next) => {
    const key = keyOf(activityId, field);
    clearTimer(key);
    setStates((prev) => ({ ...prev, [key]: next }));
    return key;
  }, [clearTimer]);

  const getState = useCallback(
    (activityId, field) => states[keyOf(activityId, field)] || "idle",
    [states],
  );

  const markSaving = useCallback(
    (activityId, field) => setState(activityId, field, "saving"),
    [setState],
  );

  const markError = useCallback(
    (activityId, field) => setState(activityId, field, "error"),
    [setState],
  );

  const clear = useCallback(
    (activityId, field) => setState(activityId, field, "idle"),
    [setState],
  );

  const markSaved = useCallback(
    (activityId, field) => {
      const key = setState(activityId, field, "saved");
      const timer = setTimeout(() => {
        timersRef.current.delete(key);
        setStates((prev) => {
          // Only revert if still 'saved' (a newer save/error may have replaced it).
          if (prev[key] !== "saved") return prev;
          return { ...prev, [key]: "idle" };
        });
      }, AUTO_REVERT_MS);
      timersRef.current.set(key, timer);
    },
    [setState],
  );

  // Cleanup-safe: clear every pending timer on unmount.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, []);

  return { getState, markSaving, markSaved, markError, clear };
}

export default useActualSaveState;
